import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal/Modal';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';
import { Textarea } from '../ui/Textarea/Textarea';
import { t } from '@/lib/i18n';

export interface AttachmentPreviewModalProps {
  /** The attachment to preview. When null, the modal is closed. */
  item: any | null;
  onClose: () => void;
  /** Optional. When provided, shows an "Edit Details" button. */
  onUpdate?: (data: { title?: string; description?: string }) => Promise<void>;
}

/**
 * Large preview dialog for a single attachment. Shows an inline renderer
 * appropriate to the MIME type (image/pdf/video/audio/text/other) plus
 * Open-in-new-tab and Download shortcuts. When `onUpdate` is passed, also
 * offers an inline Edit Details form for title + description.
 */
export function AttachmentPreviewModal({ item, onClose, onUpdate }: AttachmentPreviewModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');

  useEffect(() => {
    if (item) {
      setEditTitle(item.title || item.originalFilename || '');
      setEditDesc(item.description || '');
    }
  }, [item]);

  const handleSave = async () => {
    if (!onUpdate) return;
    try {
      await onUpdate({
        title: editTitle.trim() || undefined,
        description: editDesc.trim(),
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update:', error);
    }
  };

  if (!item) return null;

  return (
    <Modal
      isOpen={!!item}
      onClose={onClose}
      title={isEditing ? t('Edit File Details') : (item.title || item.originalFilename || t('Preview'))}
      description={!isEditing && item.description ? item.description : undefined}
      size="xl"
      className="max-w-5xl"
    >
      <div className="flex flex-col">
        {isEditing ? (
          <div className="mb-6 space-y-4">
            <Input
              label={t('Title')}
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder={t('File title')}
              className="w-full"
            />
            <Textarea
              label={t('Description')}
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              placeholder={t('File description')}
              rows={3}
            />
            <div className="flex items-center gap-3">
              <Button variant="primary" onClick={handleSave}>
                {t('Save Changes')}
              </Button>
              <Button variant="ghost" onClick={() => setIsEditing(false)}>
                {t('Cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 mb-3">
            {onUpdate ? (
              <Button variant="ghost" onClick={() => setIsEditing(true)} className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {t('Edit Details')}
              </Button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border hover:bg-gray-50 text-sm">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7m0 0v7m0-7L10 14" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10v11h11" /></svg>
                Open in new tab
              </a>
              <a href={item.url} download className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border hover:bg-gray-50 text-sm">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v12a1 1 0 001 1h3m10 0h3a1 1 0 001-1V4M8 12l4 4m0 0l4-4m-4 4V4" /></svg>
                Download
              </a>
            </div>
          </div>
        )}

        {!isEditing && (
          <div className="w-full h-[65vh] bg-gray-50 rounded border overflow-hidden">
            {(() => {
              const mime = (item.metadata?.mimeType || item.mimeType || '').toLowerCase();
              const url = item.url as string;
              if (mime.startsWith('image/')) {
                return <img src={url} alt={item.title || item.originalFilename} className="w-full h-full object-contain" />;
              }
              if (mime === 'application/pdf' || url.toLowerCase().endsWith('.pdf')) {
                return <iframe title="PDF preview" src={`${url}#toolbar=1&statusbar=0&navpanes=0`} className="w-full h-full" />;
              }
              if (mime.startsWith('video/')) {
                return <video src={url} controls className="w-full h-full object-contain" />;
              }
              if (mime.startsWith('audio/')) {
                return <div className="flex items-center justify-center h-full"><audio src={url} controls className="w-full max-w-xl" /></div>;
              }
              if (mime.startsWith('text/') || url.match(/\.(txt|csv|json|md|log)(\?.*)?$/i)) {
                return <iframe title="Text preview" src={url} className="w-full h-full bg-white" />;
              }
              return (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <svg className="w-12 h-12 text-gray-400 mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a2 2 0 000-2.828z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 13l3-3" /></svg>
                  <p className="text-sm text-gray-600">Preview not available for this file type.</p>
                  <p className="text-xs text-gray-500 mt-2">Use the buttons above to open or download.</p>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </Modal>
  );
}
